import * as Actions from '../actions/courtActions';
import Court from '../../entities/court';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type CourtAction = { 
    type: string; 
    court: Court | null; 
    courtId: string | Court; 
    partial: boolean; 
    lastFetch: number; 
    courts: Court[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Court> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Court[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Court> | null, firmId: string, userId: string, updates?: Court[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'court', 'courts', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Court>} data
 * @param {Court[]} [updates]
 * @returns {ArrayMap<Court>}
 */
function updateStorage(data: ArrayMap<Court>, updates?: Court[]): ArrayMap<Court> {
	if (logging) console.debug('⮂ courtReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Court[]} [updates]
 * @returns {ArrayMap<Court>}
 */
function updateStorageNoReturn(updates?: Court[]): void {
	if (logging) console.debug('⮂ courtReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Court[]} [data]
 * @returns {ArrayMap<Court>}
 */
function createState(data?: Court[] | ArrayMap<Court>): ArrayMap<Court> {
	if (!data) return ArrayMapRegistry.clone<Court>('court', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Court>('court', data.map(e => e));
    else return ArrayMapRegistry.clone<Court>('court', data);
}

function courts(state: ArrayMap<Court>, action: CourtAction): ArrayMap<Court> {
	if (!state) state = createState();

	let st: ArrayMap<Court>;

	switch (action.type) {
		// action.court - the new value of court with id action.id
		// action.courtId - id of the target, used if new value is null
		case Actions.COURT_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ courtReducer: FETCH_ONE_SUCCESS', action);

			if (action.court === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.courtId)) {
				st = state.clone();
				st.set(action.court);
				// state.clear();

				updateStorageNoReturn([action.court]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.courts
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.courts
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.courts
			 !partial, !stateEmpty, fetchEmpty	-> D return courts in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge courts in state newer than those in action.courts
		 */
		case Actions.COURT_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.courts.length === 0;
			const fetched = action.courts.map(court => new Court({...court, persisted: true}));

			if (logging) console.debug(`⮂ courtReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} courts with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ courtReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ courtReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Court> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Court[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(court => {
					const current = st.get(court.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && court.status === 'D') {
							// Remove it from state
							toUpdate.push(court);
							st.delete(court);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (court.addedOn > current.addedOn) {
								toUpdate.push(court);
								st.set(court);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (court.status !== 'D') {
							toUpdate.push(court);
							st.set(court);
						}
						else if (logging) console.debug('⮂ courtReducer: partialMerge: skipping deleted court not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ courtReducer: empty: courts', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ courtReducer: fullMerge: courts', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localCourt => {
                    // If this court has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localCourt.persisted) {
                        const fetchedCourt = st.get(localCourt.id);
                        if (!fetchedCourt || localCourt.addedOn > fetchedCourt.addedOn) st.set(localCourt);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.COURT_FETCH_ONE_FAILURE:
			console.error('COURT_FETCH_ONE_FAILURE', action.courtId, action.err);

			// No update to storage required
			return state;

		case Actions.COURT_FETCH_FAILURE:
			console.error('COURT_FETCH_FAILURE', action.courtId, action.err);

			// No update to storage required
			return state;

		case Actions.COURT_RESTORE: {
		    let court = new Court({...action.court});
            st = state.clone();

            const existing = st.get(court.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (court.addedOn >= existing.addedOn) {
                    st.set(court);
                    return updateStorage(st, [court]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (court.status === 'D') return state;

                st.set(court);
                return updateStorage(st, [court]);
            }
        }
            
		case Actions.COURT_SAVE: {
		    // console.debug('⮂ COURT_SAVE reducer', action.court?.id);
		    
			// clone the court
			let court = new Court({...action.court});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new court in the state
			// If this is a create, add to the queue
			const _court = st.get(court.id);

			if (!_court) st.set(court);
			else {
				if (court.addedOn >= _court.addedOn) st.set(court);
			}

			return updateStorage(st, [court]);
		}

		case Actions.COURT_SAVE_MANY: {
		    if (logging) console.debug('⮂ COURT_SAVE_MANY reducer', action.courts.map(court => court.id).join(','));
		    
			// clone the courts
			let toProcess = action.courts.map(court => new Court(court));

			st = state.clone();
			// state.clear();

			const updates: Court[] = [];

			toProcess.forEach(court => {
				// Add or update the new court in the state
				// If this is a create, add to the queue
				const existing = st.get(court.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (court.status !== 'D') {
						st.push(court);
						updates.push(court);
					}
					else if (logging) console.debug('⮂ COURT_SAVE_MANY: skipping deleted court not in state');
				}
				else {
					if (court.addedOn >= existing.addedOn) {
						st.set(court);
						updates.push(court);
					}
					else if (logging) console.debug('⮂ Skipping, existing court had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.COURT_REMOVE: {
			// clone the court
			const court = new Court({...action.court});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(court);

			return updateStorage(st, [court]);
		}

		case Actions.COURT_RESTORE_FAILURE:
		case Actions.COURT_SAVE_FAILURE: {
		    console.warn('COURT_SAVE_FAILURE reducer', action.court?.id);
		    if (!action.court?.id) {
				console.warn('COURT_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const court = ensure(Court, action.court);
			if (!court) {
				console.error('Could not find court after call to save');
				return state;
			}

			st = state.clone();

			const _courtRef = state.get(court.id);

			if (_courtRef && (_courtRef.addedOn === court.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _court = new Court({..._courtRef});
				_court.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_court.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('court', _court).then(r => {
						if (r.isErr()) console.error(`⮂ courtReducer: failed to save court with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ courtReducer: saved court with fatalError info');
					}).catch(err => console.error(`⮂ courtReducer: failed to save court with fatalError info: ${err.message}`));

				st.set(_court);
			}

			return updateStorage(st);
		}

		case Actions.COURT_SAVE_MANY_FAILURE: {
		    console.warn('COURT_SAVE_MANY_FAILURE reducer', action.courts.map(court => court.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.COURT_RESTORE_SUCCESS:
		case Actions.COURT_SAVE_SUCCESS: {
			if (!action.court?.id) {
				console.warn('COURT_SAVE_SUCCESS reducer');
                return state;
			}

			const courtRef = state.get(action.court?.id);
			if (!courtRef) {
				console.warn('Could not find court after call to save');
                return state;
			}

			st = state.clone();

			if (action.court.addedOn >= courtRef.addedOn) {
				st.set(action.court);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const court = new Court({...courtRef, persisted: true});
				st.set(court);
			}

			return updateStorage(st, [action.court]);
		}

		case Actions.COURT_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ COURT_SAVE_MANY_SUCCESS reducer', action.courts.map(court => court.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const courts: Court[] = [];

			action.courts.forEach(e => {
				const courtRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!courtRef) console.warn('Could not find court after call to save');
				else if (e.addedOn >= courtRef.addedOn) {
				    st.set(e);
				    courts.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const court = new Court({...courtRef, persisted: true});
					st.set(court);
					courts.push(court);
				}
			});

			return updateStorage(st, courts);
		}

		case Actions.COURT_REMOVE_FAILURE: {
		    console.warn('COURT_REMOVE_FAILURE action', action.court?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.COURT_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.COURT_RESTORE_MANY: {
			if (logging) console.debug('⮂ COURT_RESTORE_MANY reducer', action.courts.map(court => court.id).join(','));

			const toProcess = action.courts.map(court => new Court({...court}));
			st = state.clone();

			const updates: Court[] = [];

			toProcess.forEach(court => {
				const existing = st.get(court.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (court.addedOn >= existing.addedOn) {
						st.set(court);
						updates.push(court);
					}
					else if (logging) console.debug('⮂ COURT_RESTORE_MANY: skipping older version of court');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (court.status !== 'D') {
						st.set(court);
						updates.push(court);
					}
					else if (logging) console.debug('⮂ COURT_RESTORE_MANY: skipping deleted court not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.COURT_REMOVE_MANY: {
			if (logging) console.debug('⮂ COURT_REMOVE_MANY reducer', action.courts.map(court => court.id).join(','));

			const toProcess = action.courts.map(court => new Court({...court}));
			st = state.clone();

			toProcess.forEach(court => {
				st.delete(court);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default courts;